// Storage helpers — name kept for legacy callers, but all storage goes to
// localStorage as plaintext. The rest of the app reads with raw
// localStorage.getItem(...), so keeping values plain is what makes reads
// consistent. Mixing sessionStorage + AES with raw localStorage reads caused
// /ars/** calls to drop the uniqueCode header and 401 with missing_unique_code.
const setSessionData = (data, key) => {
    try {
        const x = typeof data === "string" ? data : JSON.stringify(data);
        localStorage.setItem(key, x);
        return true;
    } catch (error) {
        console.error('Error setting session data:', error);
        return false;
    }
};

const getSessionData = (key) => {
    try {
        return localStorage.getItem(key);
    } catch (error) {
        console.error('Error getting session data:', error);
        return null;
    }
};

const updateManifestFile = (id, token) => {
    if (!id || !token) return;

    try {
        // Clean up any existing manifest links first
        const existingLinks = document.querySelectorAll('link[rel="manifest"]');
        existingLinks.forEach(link => {
            if (link.href.startsWith('blob:')) {
                URL.revokeObjectURL(link.href);
            }
            link.remove();
        });

        var myDynamicManifest = {
            "short_name": "ACT-ARS",
            "name": "ACT-ARS",
            "icons": [
                {
                    "src": "all_city_fukkll_logo.png",
                    "type": "image/png",
                    "sizes": "192x192",
                    "purpose": "any maskable"
                },
                {
                    "src": "all_city_fukkll_logo.png",
                    "type": "image/png",
                    "sizes": "512x512",
                    "purpose": "any maskable"
                }
            ],
            "start_url": `https://devars.allcityapp.com/validateAcct/${id}/${token}`,
            "display": "standalone",
            "theme_color": "#F4BD42",
            "background_color": "#2B2929",
            "scope": "/",
            "orientation": "any"
        };

        const stringManifest = JSON.stringify(myDynamicManifest);

        // Create and append the new manifest link
        const link = document.createElement('link');
        link.rel = "manifest";
        
        // Use base64 encoding for better compatibility
        const base64Manifest = btoa(stringManifest);
        link.href = `data:application/json;base64,${base64Manifest}`;
        
        document.head.appendChild(link);

        // Update placeholder if it exists
        const placeholder = document.querySelector('#my-manifest-placeholder');
        if (placeholder) {
            placeholder.href = link.href;
        }

        return true;
    } catch (error) {
        console.error('Error updating manifest:', error);
        return false;
    }
};

const getOS = () => {
    try {
        const userAgent = window.navigator.userAgent;
        const platform = window.navigator?.userAgentData?.platform || window.navigator.platform;
        
        const platforms = {
            'Mac OS': ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'],
            'iOS': ['iPhone', 'iPad', 'iPod'],
            'Windows': ['Win32', 'Win64', 'Windows', 'WinCE'],
            'Android': [/Android/],
            'Linux': [/Linux/]
        };

        for (const [os, identifiers] of Object.entries(platforms)) {
            for (const identifier of identifiers) {
                if (typeof identifier === 'string') {
                    if (platform === identifier) return os;
                } else if (identifier.test(userAgent)) {
                    return os;
                }
            }
        }

        return 'Unknown OS';
    } catch (error) {
        console.error('Error detecting OS:', error);
        return 'Unknown OS';
    }
};

const beep = (vol, freq, duration) => {
    try {
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        oscillator.frequency.value = freq;
        oscillator.type = "square";
        
        gainNode.connect(audioContext.destination);
        gainNode.gain.value = vol * 0.01;
        
        const startTime = audioContext.currentTime;
        const stopTime = startTime + duration * 0.001;
        
        oscillator.start(startTime);
        oscillator.stop(stopTime);

        // Clean up
        setTimeout(() => {
            audioContext.close();
        }, duration + 100);
    } catch (error) {
        console.error('Error playing beep:', error);
    }
};


// GPS Accuracy Setting — localStorage plaintext.
const setGPSAccuracySetting = (setting) => {
    try {
        localStorage.setItem('gpsAccuracySetting', setting);
        return true;
    } catch (error) {
        console.error('Error setting GPS accuracy setting:', error);
        return false;
    }
};

const getGPSAccuracySetting = () => {
    try {
        const value = localStorage.getItem('gpsAccuracySetting');
        return value || 'Off'; // Default to 'Off' if not set
    } catch (error) {
        console.error('Error getting GPS accuracy setting:', error);
        return 'Off';
    }
};

// Check if GPS accuracy enforcement is required
const shouldEnforceGPSAccuracy = () => {
    const setting = getGPSAccuracySetting();
    return setting === 'Forced' || setting === 'Manual';
};

// Check if GPS accuracy should block progression
const shouldBlockForGPSAccuracy = () => {
    const setting = getGPSAccuracySetting();
    return setting === 'Forced';
};

// GPS accuracy threshold (100 meters as per requirements)
const GPS_ACCURACY_THRESHOLD = 100;

// NEW: Enhanced Location Services Checking Functions

/**
 * Check if geolocation is supported by the browser
 * @returns {boolean}
 */
const isGeolocationSupported = () => {
    return 'geolocation' in navigator;
};

/**
 * Check the current permission state for geolocation
 * @returns {Promise<string>} 'granted', 'denied', 'prompt', or 'unsupported'
 */
const checkLocationPermissionState = async () => {
    if (!isGeolocationSupported()) {
        return 'unsupported';
    }
    
    try {
        if (navigator.permissions) {
            const result = await navigator.permissions.query({ name: 'geolocation' });
            return result.state; // 'granted', 'denied', or 'prompt'
        }
        return 'unknown';
    } catch (error) {
        console.error('Error checking location permission state:', error);
        return 'unknown';
    }
};

/**
 * Check if location services are currently enabled and available
 * @returns {Promise<boolean>}
 */
const checkLocationServicesAvailability = async () => {
    if (!isGeolocationSupported()) {
        return false;
    }
    
    try {
        const permissionState = await checkLocationPermissionState();
        return permissionState === 'granted';
    } catch (error) {
        console.error('Error checking location services availability:', error);
        return false;
    }
};

/**
 * Attempt to request location permission
 * @returns {Promise<string>} 'granted', 'denied', or 'error'
 */
const requestLocationPermission = async () => {
    if (!isGeolocationSupported()) {
        return 'unsupported';
    }
    
    try {
        // Use Notification.requestPermission() pattern for geolocation
        const permission = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(
                () => resolve('granted'),
                (error) => {
                    switch (error.code) {
                        case error.PERMISSION_DENIED:
                            resolve('denied');
                            break;
                        case error.POSITION_UNAVAILABLE:
                            resolve('unavailable');
                            break;
                        case error.TIMEOUT:
                            resolve('timeout');
                            break;
                        default:
                            resolve('error');
                            break;
                    }
                },
                {
                    enableHighAccuracy: false,
                    timeout: 5000,
                    maximumAge: Infinity
                }
            );
        });
        
        return permission;
    } catch (error) {
        console.error('Error requesting location permission:', error);
        return 'error';
    }
};

/**
 * Get current GPS location with specified options
 * @param {Object} options - Geolocation options
 * @returns {Promise<Object>} Location data with coordinates and accuracy
 */
const getCurrentGPSLocation = (options = {}) => {
    const defaultOptions = {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
        ...options
    };
    
    return new Promise((resolve, reject) => {
        if (!isGeolocationSupported()) {
            reject(new Error('Geolocation not supported'));
            return;
        }
        
        navigator.geolocation.getCurrentPosition(
            (position) => {
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude,
                    accuracy: position.coords.accuracy,
                    timestamp: position.timestamp,
                    source: 'gps'
                });
            },
            (error) => {
                const errorInfo = {
                    code: error.code,
                    message: error.message,
                    type: 'geolocation_error'
                };
                
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorInfo.reason = 'permission_denied';
                        errorInfo.userMessage = 'Location permission denied';
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorInfo.reason = 'position_unavailable';
                        errorInfo.userMessage = 'Location information unavailable';
                        break;
                    case error.TIMEOUT:
                        errorInfo.reason = 'timeout';
                        errorInfo.userMessage = 'Location request timed out';
                        break;
                    default:
                        errorInfo.reason = 'unknown';
                        errorInfo.userMessage = 'Unknown location error';
                        break;
                }
                
                reject(errorInfo);
            },
            defaultOptions
        );
    });
};

/**
 * Validate GPS accuracy against threshold
 * @param {number} accuracy - GPS accuracy in meters
 * @param {number} threshold - Accuracy threshold (default: GPS_ACCURACY_THRESHOLD)
 * @returns {Object} Validation result
 */
const validateGPSAccuracy = (accuracy, threshold = GPS_ACCURACY_THRESHOLD) => {
    if (typeof accuracy !== 'number' || accuracy < 0) {
        return {
            isValid: false,
            status: 'unknown',
            message: 'Invalid accuracy value',
            accuracy: accuracy
        };
    }
    
    const isValid = accuracy <= threshold;
    let status = 'high';
    let message = `GPS accuracy is excellent (±${Math.round(accuracy)}m)`;
    
    if (accuracy > threshold * 3) {
        status = 'low';
        message = `GPS accuracy is poor (±${Math.round(accuracy)}m)`;
    } else if (accuracy > threshold) {
        status = 'medium';
        message = `GPS accuracy is below requirement (±${Math.round(accuracy)}m)`;
    }
    
    return {
        isValid,
        status,
        message,
        accuracy: accuracy,
        threshold: threshold,
        meetsRequirement: isValid
    };
};

/**
 * Get appropriate dialog action based on GPS accuracy setting
 * @param {string} setting - GPS accuracy setting ('Off', 'Manual', 'Forced')
 * @returns {Object} Dialog configuration
 */
const getLocationDialogConfig = (setting = 'Off') => {
    const configs = {
        'Off': {
            showManualEntry: true,
            showRefresh: true,
            allowClose: true,
            severity: 'info',
            title: 'Location Services',
            message: 'Location services can improve your experience'
        },
        'Manual': {
            showManualEntry: true,
            showRefresh: true,
            allowClose: true,
            severity: 'warning',
            title: 'Location Services Recommended',
            message: 'GPS accuracy is recommended for better service'
        },
        'Forced': {
            showManualEntry: false,
            showRefresh: true,
            allowClose: false,
            severity: 'error',
            title: 'Location Services Required',
            message: 'GPS accuracy is required to continue'
        }
    };
    
    return configs[setting] || configs['Off'];
};

/**
 * Store location services check result
 * @param {boolean} enabled
 * @param {string} source
 */
const setLocationServicesStatus = (enabled, source = 'check') => {
    try {
        const status = {
            enabled,
            source,
            timestamp: Date.now(),
            checked: true
        };
        localStorage.setItem('locationServicesStatus', JSON.stringify(status));
        return true;
    } catch (error) {
        console.error('Error setting location services status:', error);
        return false;
    }
};

/**
 * Get stored location services status
 * @returns {Object|null}
 */
const getLocationServicesStatus = () => {
    try {
        const raw = localStorage.getItem('locationServicesStatus');
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        console.error('Error getting location services status:', error);
        return null;
    }
};

/**
 * Converts full state names to their standard abbreviations
 * @param {string} fullStateName - The full state name (e.g., "Arizona")
 * @returns {string} - The abbreviated state code (e.g., "AZ")
 */
const getStateAbbreviation = (fullStateName) => {
    if (!fullStateName || typeof fullStateName !== 'string') {
        return fullStateName;
    }

    const stateMap = {
        'Alabama': 'AL',
        'Alaska': 'AK',
        'Arizona': 'AZ',
        'Arkansas': 'AR',
        'California': 'CA',
        'Colorado': 'CO',
        'Connecticut': 'CT',
        'Delaware': 'DE',
        'Florida': 'FL',
        'Georgia': 'GA',
        'Hawaii': 'HI',
        'Idaho': 'ID',
        'Illinois': 'IL',
        'Indiana': 'IN',
        'Iowa': 'IA',
        'Kansas': 'KS',
        'Kentucky': 'KY',
        'Louisiana': 'LA',
        'Maine': 'ME',
        'Maryland': 'MD',
        'Massachusetts': 'MA',
        'Michigan': 'MI',
        'Minnesota': 'MN',
        'Mississippi': 'MS',
        'Missouri': 'MO',
        'Montana': 'MT',
        'Nebraska': 'NE',
        'Nevada': 'NV',
        'New Hampshire': 'NH',
        'New Jersey': 'NJ',
        'New Mexico': 'NM',
        'New York': 'NY',
        'North Carolina': 'NC',
        'North Dakota': 'ND',
        'Ohio': 'OH',
        'Oklahoma': 'OK',
        'Oregon': 'OR',
        'Pennsylvania': 'PA',
        'Rhode Island': 'RI',
        'South Carolina': 'SC',
        'South Dakota': 'SD',
        'Tennessee': 'TN',
        'Texas': 'TX',
        'Utah': 'UT',
        'Vermont': 'VT',
        'Virginia': 'VA',
        'Washington': 'WA',
        'West Virginia': 'WV',
        'Wisconsin': 'WI',
        'Wyoming': 'WY',
        'District of Columbia': 'DC',
        'Puerto Rico': 'PR',
        'American Samoa': 'AS',
        'Guam': 'GU',
        'Northern Mariana Islands': 'MP',
        'U.S. Virgin Islands': 'VI'
    };

    // Try exact match first
    const exactMatch = stateMap[fullStateName];
    if (exactMatch) {
        return exactMatch;
    }

    // Try case-insensitive match
    const lowerCaseInput = fullStateName.toLowerCase();
    for (const [stateName, abbreviation] of Object.entries(stateMap)) {
        if (stateName.toLowerCase() === lowerCaseInput) {
            return abbreviation;
        }
    }

    // If it's already an abbreviation, return as-is
    const allAbbreviations = Object.values(stateMap);
    const upperCaseInput = fullStateName.toUpperCase();
    if (allAbbreviations.includes(upperCaseInput)) {
        return upperCaseInput;
    }

    // If no match found, return the original value
    console.warn(`State abbreviation not found for: ${fullStateName}`);
    return fullStateName;
};

export {
    setSessionData,
    getSessionData,
    updateManifestFile,
    getOS,
    beep,
    setGPSAccuracySetting,
    getGPSAccuracySetting,
    shouldEnforceGPSAccuracy,
    shouldBlockForGPSAccuracy,
    GPS_ACCURACY_THRESHOLD,
    getStateAbbreviation,
    // NEW: Enhanced location services functions
    isGeolocationSupported,
    checkLocationPermissionState,
    checkLocationServicesAvailability,
    requestLocationPermission,
    getCurrentGPSLocation,
    validateGPSAccuracy,
    getLocationDialogConfig,
    setLocationServicesStatus,
    getLocationServicesStatus
};